import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/I18nContext";
import translations from "../i18n/translations";
import * as programsService from "../services/programs";
import { fetchPublicTeachers } from "../services/teachersPublic";
import { apiBaseUrl } from "../services/auth";
import "./ProgramDetails.css";

const DEFAULT_ACCENT_COLOR = "#4f7cff";

const normalizeHexColor = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return DEFAULT_ACCENT_COLOR;
  const token = raw.startsWith("#") ? raw : `#${raw}`;
  return /^#[0-9a-fA-F]{6}$/.test(token) ? token : DEFAULT_ACCENT_COLOR;
};

const ICON_OPTIONS = [
  {
    labelKey: "programDetails.icons.reading",
    fallbackLabel: "Reading",
    value: "fa-solid fa-book-open-reader",
  },
  {
    labelKey: "programDetails.icons.graduation",
    fallbackLabel: "Graduation",
    value: "fa-solid fa-graduation-cap",
  },
  {
    labelKey: "programDetails.icons.math",
    fallbackLabel: "Math",
    value: "fa-solid fa-square-root-variable",
  },
  {
    labelKey: "programDetails.icons.science",
    fallbackLabel: "Science",
    value: "fa-solid fa-flask",
  },
  {
    labelKey: "programDetails.icons.language",
    fallbackLabel: "Language",
    value: "fa-solid fa-language",
  },
  {
    labelKey: "programDetails.icons.coding",
    fallbackLabel: "Coding",
    value: "fa-solid fa-laptop-code",
  },
  {
    labelKey: "programDetails.icons.islamicStudies",
    fallbackLabel: "Islamic studies",
    value: "fa-solid fa-mosque",
  },
  {
    labelKey: "programDetails.icons.atom",
    fallbackLabel: "Atom",
    value: "fa-solid fa-atom",
  },
  {
    labelKey: "programDetails.icons.design",
    fallbackLabel: "Design",
    value: "fa-solid fa-compass-drafting",
  },
  {
    labelKey: "programDetails.icons.writing",
    fallbackLabel: "Writing",
    value: "fa-solid fa-pen-nib",
  },
  {
    labelKey: "programDetails.icons.globe",
    fallbackLabel: "Globe",
    value: "fa-solid fa-globe",
  },
  {
    labelKey: "programDetails.icons.star",
    fallbackLabel: "Star",
    value: "fa-solid fa-star",
  },
  // Legacy values (kept for backward compatibility with existing programs)
  {
    labelKey: "programDetails.icons.bookLegacy",
    fallbackLabel: "Book (legacy)",
    value: "fa-solid fa-book-open",
  },
  {
    labelKey: "programDetails.icons.scienceLegacy",
    fallbackLabel: "Science (legacy)",
    value: "fa-solid fa-biohazard",
  },
];

const slugifyProgramId = (value, fallback) => {
  const base = typeof value === "string" ? value : "";
  const slug = base
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\u0600-\u06FF]+/gi, "-")
    .replace(/^-+|-+$/g, "");
  return slug || `program-${fallback}`;
};

const mapFallbackPrograms = (items) => {
  const list = Array.isArray(items) ? items : [];
  const palette = [
    "#7d52ff",
    "#4f7cff",
    "#ff7c3e",
    "#10b38b",
    "#b9a62d",
    "#ff5b5b",
  ];
  const icons = [
    "fa-solid fa-book-open-reader",
    "fa-solid fa-graduation-cap",
    "fa-solid fa-square-root-variable",
    "fa-solid fa-flask",
    "fa-solid fa-language",
    "fa-solid fa-laptop-code",
  ];
  return list.map((item, index) => {
    const slug = slugifyProgramId(item?.title, index + 1);
    return {
      id: slug,
      slug,
      title: item?.title || "",
      description: item?.description || item?.slogan || "",
      long_description: item?.longDescription || "",
      image_url: item?.image || "",
      cta_label: item?.ctaLabel || item?.cta || "",
      cta_link: item?.ctaLink || "",
      points: Array.isArray(item?.list) ? item.list : [],
      levels: [],
      accent_color: palette[index % palette.length],
      icon_class: icons[index % icons.length],
      display_order: index,
      is_active: true,
    };
  });
};

const toTextarea = (items) => (Array.isArray(items) ? items.join("\n") : "");

const toList = (value) =>
  (value || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

const resolveAssetUrl = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (raw.startsWith("data:") || raw.startsWith("blob:")) return raw;

  // If the backend stored an absolute URL, but the frontend is configured to talk
  // to a different backend host, prefer the configured backend for /media assets.
  // This avoids broken images when running behind proxies/containers.
  if (/^https?:\/\//i.test(raw)) {
    try {
      const parsed = new URL(raw);
      if (parsed.pathname && parsed.pathname.startsWith("/media/")) {
        return `${apiBaseUrl}${parsed.pathname}`;
      }
      return raw;
    } catch (e) {
      return raw;
    }
  }
  if (raw.startsWith("/")) {
    return `${apiBaseUrl}${raw}`;
  }
  return raw;
};

export default function ProgramDetails() {
  const { programId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, accessToken } = useAuth();
  const { t, language } = useTranslation();
  const dir = translations[language]?.meta?.dir || "ltr";

  const asArray = (value) => (Array.isArray(value) ? value : []);

  const subjectsGridItems = useMemo(
    () =>
      asArray(
        t("landingPage.subjectsGrid.items", {
          returnObjects: true,
          lng: language,
        }),
      ),
    [t, language],
  );

  const programsFallback = useMemo(
    () =>
      asArray(
        t("landingPage.programs.items", { returnObjects: true, lng: language }),
      ),
    [t, language],
  );

  const resolvedPrograms = useMemo(
    () => (subjectsGridItems.length ? subjectsGridItems : programsFallback),
    [subjectsGridItems, programsFallback],
  );

  const defaultPrograms = useMemo(
    () => mapFallbackPrograms(resolvedPrograms),
    [resolvedPrograms],
  );

  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [teacherOptions, setTeacherOptions] = useState([]);
  const [levelUploadBusy, setLevelUploadBusy] = useState(null);

  const levelsCatalog = useMemo(
    () =>
      asArray(
        t("landingPage.levels.items", { returnObjects: true, lng: language }),
      ),
    [t, language],
  );

  const selectedLevelId = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    const raw = params.get("level") || "";
    return String(raw).trim();
  }, [location.search]);

  const loadPrograms = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = await programsService.fetchPrograms({
        accessToken: user ? accessToken : undefined,
      });
      setPrograms(payload.programs || []);
    } catch (err) {
      setPrograms(defaultPrograms);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [accessToken, defaultPrograms, user]);

  useEffect(() => {
    let ignore = false;
    (async () => {
      if (ignore) return;
      await loadPrograms();
    })();
    return () => {
      ignore = true;
    };
  }, [loadPrograms]);

  const selectedProgram = useMemo(
    () =>
      programs.find(
        (program) =>
          program.slug === programId || String(program.id) === programId,
      ),
    [programs, programId],
  );

  const catalogLevelsByGrade = useMemo(() => {
    const catalog = Array.isArray(levelsCatalog) ? levelsCatalog : [];
    return new Map(
      catalog
        .map((item) => {
          const grade = String(item?.grade ?? "").trim();
          if (!grade) return null;
          return [grade, { grade, label: String(item?.label ?? "").trim() }];
        })
        .filter(Boolean),
    );
  }, [levelsCatalog]);

  const programLevels = useMemo(() => {
    const raw = Array.isArray(selectedProgram?.levels)
      ? selectedProgram.levels
      : [];
    const normalized = [];
    const seen = new Set();

    raw.forEach((item) => {
      if (item == null) return;

      if (typeof item === "string") {
        const grade = String(item).trim();
        if (!grade || seen.has(grade)) return;
        seen.add(grade);
        const fallbackLabel = catalogLevelsByGrade.get(grade)?.label || "";
        normalized.push({
          grade,
          label: fallbackLabel,
          description: "",
          image_url: "",
        });
        return;
      }

      if (typeof item === "object") {
        const tmpId = String(item.__tmpId ?? "").trim();
        const grade = String(item.grade ?? item.id ?? "").trim();

        // Allow draft rows without a grade while editing.
        if (!grade && !tmpId) return;
        if (grade && seen.has(grade)) return;
        if (grade) seen.add(grade);

        const fallbackLabel = catalogLevelsByGrade.get(grade)?.label || "";
        const label = String(item.label ?? item.title ?? "").trim();
        const description = String(item.description ?? "").trim();
        const image_url = String(item.image_url ?? item.image ?? "").trim();
        normalized.push({
          __tmpId: tmpId || undefined,
          grade,
          label: label || fallbackLabel,
          description,
          image_url,
        });
      }
    });

    return normalized;
  }, [catalogLevelsByGrade, selectedProgram?.levels]);

  const visibleLevels = useMemo(() => {
    if (!selectedLevelId) return programLevels;
    return programLevels.filter((level) => level.grade === selectedLevelId);
  }, [programLevels, selectedLevelId]);

  const isTeacher = Boolean(user && user.role === "teacher");
  const isAdmin = Boolean(
    user && (user.role === "admin" || user.role === "superadmin"),
  );
  const isParent = Boolean(user && user.role === "parent");
  const canEditPrograms = Boolean(user && (isTeacher || isAdmin));
  const isPublicView = !user;

  const shouldRequireLevel = Boolean(isPublicView || isParent);

  const programLinkForUser = (program) => {
    const targetId = program?.slug || program?.id;
    if (!targetId) return "/";

    if (canEditPrograms) {
      return `/programs/${targetId}?edit=1`;
    }

    if (shouldRequireLevel) {
      return `/programs/${targetId}/select-level`;
    }

    return `/programs/${targetId}`;
  };

  useEffect(() => {
    if (!programId) return;
    if (!selectedProgram) return;
    if (selectedLevelId) return;
    if (!shouldRequireLevel) return;
    if (canEditPrograms) return;

    const targetId = selectedProgram.slug || selectedProgram.id || programId;
    navigate(`/programs/${targetId}/select-level`, { replace: true });
  }, [
    canEditPrograms,
    navigate,
    programId,
    selectedLevelId,
    selectedProgram,
    shouldRequireLevel,
  ]);

  useEffect(() => {
    let ignore = false;
    (async () => {
      if (!isAdmin) return;
      try {
        const payload = await fetchPublicTeachers();
        if (ignore) return;
        setTeacherOptions(payload.teachers || []);
      } catch (err) {
        if (ignore) return;
        setTeacherOptions([]);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [isAdmin]);

  const shouldAutoEdit = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return params.get("edit") === "1";
  }, [location.search]);

  useEffect(() => {
    if (!canEditPrograms) return;
    if (!selectedProgram) return;
    if (shouldAutoEdit) {
      setIsEditing(true);
    }
  }, [canEditPrograms, selectedProgram, shouldAutoEdit]);

  const updateProgram = (patch) => {
    setPrograms((prev) =>
      prev.map((program) =>
        program.slug === programId || String(program.id) === programId
          ? { ...program, ...patch }
          : program,
      ),
    );
  };

  const addProgramLevel = () => {
    const tmpId = `lvl-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    updateProgram({
      levels: [
        ...programLevels,
        {
          __tmpId: tmpId,
          grade: "",
          label: "",
          description: "",
          image_url: "",
        },
      ],
    });
  };

  const removeProgramLevelAt = (index) => {
    updateProgram({
      levels: programLevels.filter((_, idx) => idx !== index),
    });
  };

  const patchProgramLevelAt = (index, patch) => {
    updateProgram({
      levels: programLevels.map((level, idx) =>
        idx === index ? { ...level, ...patch } : level,
      ),
    });
  };

  const uploadLevelImageAt = async (index, file) => {
    if (!accessToken) {
      setError(t("programDetails.errors.unauthorized"));
      return;
    }
    if (!file) return;

    setError(null);
    setLevelUploadBusy(index);
    try {
      const payload = await programsService.uploadLevelImage(accessToken, file);
      const url = String(payload?.url || payload?.image_url || "").trim();
      if (!url) {
        throw new Error(t("programDetails.errors.uploadFailed"));
      }
      patchProgramLevelAt(index, { image_url: url });
    } catch (err) {
      setError(err.message);
    } finally {
      setLevelUploadBusy(null);
    }
  };

  const handleSave = async () => {
    if (!selectedProgram || !accessToken) return;
    setError(null);
    try {
      const incompleteLevel = programLevels.find(
        (level) => !String(level?.grade ?? "").trim(),
      );
      if (incompleteLevel) {
        setError(t("programDetails.errors.levelsRequireGrade"));
        return;
      }

      const langKey = String(language || "en");
      const i18nPatch = {
        title: { [langKey]: selectedProgram.title || "" },
        description: { [langKey]: selectedProgram.description || "" },
        long_description: { [langKey]: selectedProgram.long_description || "" },
        cta_label: { [langKey]: selectedProgram.cta_label || "" },
      };

      const levelsPayload = programLevels
        .map((level) => {
          const grade = String(level?.grade ?? "").trim();
          const label = String(level?.label ?? "").trim();
          const description = String(level?.description ?? "").trim();
          const image_url = String(level?.image_url ?? "").trim();
          return { grade, label, description, image_url };
        })
        .filter((level) => level.grade);

      const payload = {
        title: selectedProgram.title,
        description: selectedProgram.description,
        long_description: selectedProgram.long_description,
        image_url: selectedProgram.image_url,
        cta_label: selectedProgram.cta_label,
        cta_link: selectedProgram.cta_link,
        points: selectedProgram.points || [],
        levels: levelsPayload,
        accent_color: selectedProgram.accent_color || "",
        icon_class: selectedProgram.icon_class || "",
        display_order: selectedProgram.display_order || 0,
        is_active: selectedProgram.is_active !== false,
        i18n: i18nPatch,
      };

      if (isAdmin) {
        payload.assigned_teacher_id =
          selectedProgram.assigned_teacher_id || null;
      }

      if (selectedProgram.__isDraft) {
        const title = String(selectedProgram.title || "").trim();
        if (!title) {
          setError(t("programDetails.errors.titleRequired"));
          return;
        }

        const created = await programsService.createProgram(
          accessToken,
          payload,
        );
        setPrograms((prev) =>
          prev
            .filter(
              (program) =>
                !(
                  (program.slug === programId ||
                    String(program.id) === programId) &&
                  program.__isDraft
                ),
            )
            .concat(created),
        );
        setIsEditing(false);
        navigate(`/programs/${created.slug || created.id}`);
        await loadPrograms();
        return;
      }

      await programsService.updateProgram(
        accessToken,
        selectedProgram.slug,
        payload,
      );
      setIsEditing(false);
      await loadPrograms();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCreate = async () => {
    if (!isAdmin) return;
    setError(null);

    const draftSlug = `__draft__${Date.now()}`;
    const draft = {
      id: draftSlug,
      slug: draftSlug,
      __isDraft: true,
      title: t("programDetails.draft.title"),
      description: "",
      long_description: "",
      image_url: "",
      cta_label: "",
      cta_link: "",
      points: [],
      levels: [],
      accent_color: "",
      icon_class: "fa-solid fa-book-open-reader",
      display_order: 0,
      is_active: true,
      assigned_teacher_id: null,
    };

    setPrograms((prev) => [...prev, draft]);
    setIsEditing(true);
    navigate(`/programs/${draftSlug}?edit=1`);
  };

  const handleCancelEdits = () => {
    if (selectedProgram?.__isDraft) {
      setPrograms((prev) =>
        prev.filter(
          (program) =>
            !(
              (program.slug === programId ||
                String(program.id) === programId) &&
              program.__isDraft
            ),
        ),
      );
      setIsEditing(false);
      navigate("/dashboard");
      return;
    }

    setIsEditing(false);
    loadPrograms();
  };

  const handleDelete = async () => {
    if (!isAdmin || !accessToken || !selectedProgram) return;
    const message = t("programDetails.confirm.deleteProgram");
    if (typeof window !== "undefined" && !window.confirm(message)) {
      return;
    }
    setDeleteBusy(true);
    setError(null);
    try {
      await programsService.deleteProgram(accessToken, selectedProgram.slug);
      navigate("/dashboard");
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleteBusy(false);
    }
  };

  if (loading) {
    return (
      <div
        className={`program-details${isPublicView ? " program-details--public" : ""}`}
        dir={dir}
      >
        <div className="program-details__shell">
          <div className="program-details__loading">
            {t("programDetails.loading")}
          </div>
        </div>
      </div>
    );
  }

  if (!selectedProgram) {
    return (
      <div
        className={`program-details${isPublicView ? " program-details--public" : ""}`}
        dir={dir}
      >
        <div className="program-details__shell">
          <header className="program-details__header">
            <div>
              <p className="program-details__eyebrow">
                {t("programDetails.eyebrow")}
              </p>
              <h1>{t("programDetails.notFound.title")}</h1>
              <p>{t("programDetails.notFound.hint")}</p>
            </div>
            <div className="program-details__header-actions">
              {!isPublicView ? (
                <Link to="/dashboard" className="btn ghost">
                  {t("programDetails.actions.backToDashboard")}
                </Link>
              ) : (
                <Link to="/" className="btn ghost">
                  {t("programDetails.actions.backToHome")}
                </Link>
              )}
              {isAdmin && (
                <button
                  type="button"
                  className="btn primary"
                  onClick={handleCreate}
                >
                  {t("programDetails.actions.newProgram")}
                </button>
              )}
            </div>
          </header>
          <div className="program-details__content">
            <aside className="program-details__sidebar">
              <h3>{t("programDetails.sidebar.allPrograms")}</h3>
              <ul>
                {programs.map((program) => (
                  <li key={program.slug || program.id}>
                    <Link to={programLinkForUser(program)}>
                      {program.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </aside>
          </div>
        </div>
      </div>
    );
  }

  const displayDescription =
    selectedProgram.long_description || selectedProgram.description;

  const accentColor = normalizeHexColor(selectedProgram.accent_color);
  const knownIconValues = ICON_OPTIONS.map((opt) => opt.value);
  const iconClassRaw = String(selectedProgram.icon_class || "").trim();
  const iconSelectValue = knownIconValues.includes(iconClassRaw)
    ? iconClassRaw
    : "__custom__";
  const iconPreviewClass = iconClassRaw || "fa-solid fa-globe";

  if (isPublicView) {
    return (
      <div className="program-details program-details--public" dir={dir}>
        <div className="program-details__shell">
          <header
            className="program-details__hero"
            style={{ "--program-accent": accentColor }}
          >
            <div className="program-details__heroTop">
              <Link to="/" className="btn ghost">
                {t("programDetails.actions.backToHome")}
              </Link>
            </div>
            <div className="program-details__heroBody">
              <div className="program-details__heroIcon" aria-hidden="true">
                <i className={iconPreviewClass} />
              </div>
              <div className="program-details__heroCopy">
                <p className="program-details__eyebrow">
                  {t("programDetails.eyebrow")}
                </p>
                <h1>{selectedProgram.title}</h1>
                {selectedProgram.description ? (
                  <p className="program-details__subtitle">
                    {selectedProgram.description}
                  </p>
                ) : null}
              </div>
            </div>
          </header>

          {error && (
            <div className="program-details__error" role="alert">
              {error}
            </div>
          )}

          <div className="program-details__content program-details__content--public">
            <main className="program-details__main">
              {selectedProgram.image_url ? (
                <div className="program-details__image-wrap">
                  <img
                    src={resolveAssetUrl(selectedProgram.image_url)}
                    alt={selectedProgram.title}
                    loading="lazy"
                  />
                </div>
              ) : null}

              {displayDescription ? (
                <section className="program-details__section">
                  <h2>{t("programDetails.sections.overview")}</h2>
                  <p>{displayDescription}</p>
                </section>
              ) : null}

              {selectedProgram.points?.length ? (
                <section className="program-details__section">
                  <h2>{t("programDetails.sections.learning")}</h2>
                  <ul className="program-details__list">
                    {selectedProgram.points.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </section>
              ) : null}

              {visibleLevels.length ? (
                <section className="program-details__section">
                  <h2>
                    {t(
                      selectedLevelId
                        ? "programDetails.sections.levels.single"
                        : "programDetails.sections.levels.plural",
                    )}
                  </h2>
                  {!selectedLevelId ? (
                    <p className="program-details__muted">
                      {t("programDetails.levels.pickFirst")}
                    </p>
                  ) : null}
                  <div className="program-details__topics">
                    {visibleLevels.map((level) => (
                      <article
                        key={`level-${level.grade}`}
                        className="program-details__topic"
                      >
                        {level.image_url ? (
                          <div className="program-details__topicImage">
                            <img
                              src={resolveAssetUrl(level.image_url)}
                              alt={t("programDetails.levels.levelImageAlt", {
                                grade: level.grade,
                              })}
                              loading="lazy"
                            />
                          </div>
                        ) : null}
                        <div className="program-details__topicBody">
                          <h3>
                            {t("programDetails.levels.levelTitle", {
                              grade: level.grade,
                            })}
                          </h3>
                          {level.label ? <p>{level.label}</p> : null}
                          {level.description ? (
                            <p>{level.description}</p>
                          ) : null}
                        </div>
                      </article>
                    ))}
                  </div>
                  {!selectedLevelId ? (
                    <div className="program-details__cta">
                      <Link
                        to={programLinkForUser(selectedProgram)}
                        className="btn ghost"
                      >
                        {t("programDetails.levels.chooseLevel")}
                      </Link>
                    </div>
                  ) : null}
                </section>
              ) : null}

              <div className="program-details__cta">
                <Link
                  to={selectedProgram.cta_link || "/signup"}
                  className="btn primary"
                >
                  {selectedProgram.cta_label ||
                    t("programDetails.cta.enrollNow")}
                </Link>
              </div>

              {programs.length > 1 ? (
                <section className="program-details__section">
                  <h2>{t("programDetails.sections.morePrograms")}</h2>
                  <div className="program-details__morePrograms">
                    {programs
                      .filter((p) => (p.slug || p.id) !== selectedProgram.slug)
                      .slice(0, 6)
                      .map((p) => (
                        <Link
                          key={p.slug || p.id}
                          to={programLinkForUser(p)}
                          className="program-details__moreProgram"
                        >
                          {p.title}
                        </Link>
                      ))}
                  </div>
                </section>
              ) : null}
            </main>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="program-details" dir={dir}>
      <div className="program-details__shell">
        <header className="program-details__header">
          <div>
            <p className="program-details__eyebrow">
              {t("programDetails.eyebrow")}
            </p>
            <h1>{selectedProgram.title}</h1>
            {selectedProgram.description ? (
              <p className="program-details__subtitle">
                {selectedProgram.description}
              </p>
            ) : null}
          </div>
          <div className="program-details__header-actions">
            <Link to="/dashboard" className="btn ghost">
              {t("programDetails.actions.backToDashboard")}
            </Link>
            {isAdmin && (
              <button
                type="button"
                className="btn primary"
                onClick={handleCreate}
              >
                {t("programDetails.actions.newProgram")}
              </button>
            )}
          </div>
        </header>

        {error && (
          <div className="program-details__error" role="alert">
            {error}
          </div>
        )}

        {canEditPrograms && isEditing ? (
          <div className="program-details__content program-details__content--editor">
            <main className="program-details__main program-details__main--editor">
              <div
                className="program-details__editor program-details__editor--full"
                style={{ "--program-accent": accentColor }}
              >
                <div className="program-details__editor-head">
                  <h4>{t("programDetails.editor.title")}</h4>
                  <div className="program-details__editor-actions">
                    {isAdmin ? (
                      <button
                        type="button"
                        className="btn danger"
                        onClick={handleDelete}
                        disabled={deleteBusy || selectedProgram?.__isDraft}
                      >
                        {t("programDetails.editor.delete")}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="btn primary"
                      onClick={handleSave}
                    >
                      {t("programDetails.editor.save")}
                    </button>
                    <button
                      type="button"
                      className="btn ghost"
                      onClick={handleCancelEdits}
                    >
                      {t("programDetails.editor.cancel")}
                    </button>
                  </div>
                </div>

                <div className="program-details__form">
                  <label>
                    {t("programDetails.fields.accentColor")}
                    <div className="program-details__inline-row">
                      <input
                        className="program-details__color"
                        type="color"
                        value={accentColor}
                        onChange={(event) =>
                          updateProgram({
                            accent_color: normalizeHexColor(event.target.value),
                          })
                        }
                      />
                      <input
                        value={selectedProgram.accent_color || ""}
                        placeholder="#4f7cff"
                        onChange={(event) =>
                          updateProgram({ accent_color: event.target.value })
                        }
                      />
                    </div>
                  </label>

                  <label>
                    {t("programDetails.fields.icon")}
                    <div className="program-details__inline-row">
                      <div
                        className="program-details__icon-preview"
                        aria-hidden="true"
                      >
                        <i className={iconPreviewClass} />
                      </div>
                      <select
                        value={iconSelectValue}
                        onChange={(event) => {
                          const value = event.target.value;
                          if (value && value !== "__custom__") {
                            updateProgram({ icon_class: value });
                          }
                        }}
                      >
                        <option value="__custom__">
                          {t("programDetails.fields.custom")}
                        </option>
                        {ICON_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {t(opt.labelKey, {
                              defaultValue: opt.fallbackLabel,
                            })}
                          </option>
                        ))}
                      </select>
                    </div>
                    <input
                      value={selectedProgram.icon_class || ""}
                      placeholder="fa-solid fa-book-open-reader"
                      onChange={(event) =>
                        updateProgram({ icon_class: event.target.value })
                      }
                    />
                  </label>

                  {isAdmin ? (
                    <label>
                      {t("programDetails.fields.assignedTeacher")}
                      <select
                        value={selectedProgram.assigned_teacher_id || ""}
                        onChange={(event) => {
                          const raw = event.target.value;
                          updateProgram({
                            assigned_teacher_id: raw ? Number(raw) : null,
                          });
                        }}
                      >
                        <option value="">
                          {t("programDetails.fields.unassigned")}
                        </option>
                        {teacherOptions.map((teacher) => (
                          <option key={teacher.id} value={teacher.id}>
                            {teacher.full_name ||
                              teacher.first_name ||
                              t("programDetails.fields.teacherFallback")}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : null}

                  <label>
                    {t("programDetails.fields.title")}
                    <input
                      value={selectedProgram.title}
                      onChange={(event) =>
                        updateProgram({ title: event.target.value })
                      }
                    />
                  </label>

                  <label>
                    {t("programDetails.fields.shortDescription")}
                    <textarea
                      rows={3}
                      value={selectedProgram.description}
                      onChange={(event) =>
                        updateProgram({ description: event.target.value })
                      }
                    />
                  </label>

                  <label>
                    {t("programDetails.fields.details")}
                    <textarea
                      rows={5}
                      value={selectedProgram.long_description}
                      onChange={(event) =>
                        updateProgram({ long_description: event.target.value })
                      }
                    />
                  </label>

                  <label>
                    {t("programDetails.fields.keyPoints")}
                    <textarea
                      rows={5}
                      value={toTextarea(selectedProgram.points)}
                      onChange={(event) =>
                        updateProgram({ points: toList(event.target.value) })
                      }
                    />
                  </label>

                  <div className="program-details__topicsEditor">
                    <div className="program-details__topicsEditorHead">
                      <strong>{t("programDetails.fields.levels")}</strong>
                      {shouldRequireLevel ? (
                        <Link
                          to={`/programs/${selectedProgram.slug || selectedProgram.id}/select-level`}
                          className="btn ghost"
                        >
                          {t("programDetails.fields.previewLevelsPage")}
                        </Link>
                      ) : null}
                    </div>

                    <div className="program-details__levelsGrid">
                      {programLevels.map((level, index) => (
                        <div
                          key={`level-row-${level.__tmpId || level.grade || index}`}
                          className="program-details__levelOption"
                        >
                          <div className="program-details__levelOptionBody">
                            <div className="program-details__levelOptionHead">
                              <span className="program-details__levelOptionTitle">
                                {t("programDetails.fields.levelRowTitle", {
                                  index: index + 1,
                                })}
                              </span>
                              <button
                                type="button"
                                className="btn danger"
                                onClick={() => removeProgramLevelAt(index)}
                              >
                                {t("programDetails.fields.remove")}
                              </button>
                            </div>

                            <div className="program-details__levelOptionFields">
                              <label>
                                {t("programDetails.fields.grade")}
                                <input
                                  value={level.grade}
                                  onChange={(event) =>
                                    patchProgramLevelAt(index, {
                                      grade: event.target.value,
                                    })
                                  }
                                  placeholder={t(
                                    "programDetails.placeholders.grade",
                                  )}
                                />
                              </label>

                              <label>
                                {t("programDetails.fields.label")}
                                <input
                                  value={level.label}
                                  onChange={(event) =>
                                    patchProgramLevelAt(index, {
                                      label: event.target.value,
                                    })
                                  }
                                  placeholder={t(
                                    "programDetails.placeholders.levelName",
                                  )}
                                />
                              </label>

                              <label>
                                {t("programDetails.fields.description")}
                                <textarea
                                  rows={3}
                                  value={level.description}
                                  onChange={(event) =>
                                    patchProgramLevelAt(index, {
                                      description: event.target.value,
                                    })
                                  }
                                />
                              </label>
                            </div>

                            <div className="program-details__levelOptionMedia">
                              <div className="program-details__levelImageUpload">
                                <div className="program-details__levelImageUploadLabel">
                                  {t("programDetails.fields.uploadImage")}
                                </div>
                                <input
                                  id={`level-image-upload-${
                                    level.__tmpId || level.grade || index
                                  }`}
                                  type="file"
                                  accept="image/*"
                                  disabled={levelUploadBusy === index}
                                  className="program-details__levelImageUploadInput"
                                  onChange={(event) => {
                                    const file = event.target.files?.[0];
                                    if (file) {
                                      uploadLevelImageAt(index, file);
                                    }
                                  }}
                                />
                                <label
                                  htmlFor={`level-image-upload-${
                                    level.__tmpId || level.grade || index
                                  }`}
                                  className={`program-details__levelImageUploadBox${
                                    levelUploadBusy === index
                                      ? " program-details__levelImageUploadBox--disabled"
                                      : ""
                                  }`}
                                >
                                  <span className="program-details__levelImageUploadText">
                                    {levelUploadBusy === index
                                      ? t("programDetails.fields.uploading")
                                      : t("programDetails.fields.chooseFile")}
                                  </span>
                                  {level.image_url ? (
                                    <img
                                      className="program-details__levelImageUploadThumb"
                                      src={resolveAssetUrl(level.image_url)}
                                      alt={t(
                                        "programDetails.levels.levelImageAlt",
                                        { grade: level.grade || index + 1 },
                                      )}
                                      loading="lazy"
                                    />
                                  ) : (
                                    <span className="program-details__levelImageUploadHint">
                                      PNG/JPG
                                    </span>
                                  )}
                                </label>
                              </div>

                              {level.image_url ? (
                                <button
                                  type="button"
                                  className="btn ghost"
                                  onClick={() =>
                                    patchProgramLevelAt(index, {
                                      image_url: "",
                                    })
                                  }
                                >
                                  {t("programDetails.fields.removeImage")}
                                </button>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      ))}

                      <button
                        type="button"
                        className="btn ghost"
                        onClick={addProgramLevel}
                      >
                        {t("programDetails.actions.addLevel")}
                      </button>
                    </div>
                  </div>

                  <label>
                    {t("programDetails.fields.imageUrl")}
                    <input
                      value={selectedProgram.image_url}
                      onChange={(event) =>
                        updateProgram({ image_url: event.target.value })
                      }
                    />
                  </label>

                  <label>
                    {t("programDetails.fields.ctaLabel")}
                    <input
                      value={selectedProgram.cta_label}
                      onChange={(event) =>
                        updateProgram({ cta_label: event.target.value })
                      }
                    />
                  </label>

                  <label>
                    {t("programDetails.fields.ctaLink")}
                    <input
                      value={selectedProgram.cta_link}
                      onChange={(event) =>
                        updateProgram({ cta_link: event.target.value })
                      }
                    />
                  </label>

                  <label>
                    {t("programDetails.fields.displayOrder")}
                    <input
                      type="number"
                      value={selectedProgram.display_order || 0}
                      onChange={(event) =>
                        updateProgram({
                          display_order: Number(event.target.value || 0),
                        })
                      }
                    />
                  </label>

                  <label>
                    <span>{t("programDetails.fields.active")}</span>
                    <input
                      type="checkbox"
                      checked={selectedProgram.is_active !== false}
                      onChange={(event) =>
                        updateProgram({ is_active: event.target.checked })
                      }
                    />
                  </label>
                </div>
              </div>
            </main>
          </div>
        ) : (
          <div className="program-details__content">
            <main className="program-details__main">
              {selectedProgram.image_url ? (
                <div className="program-details__image-wrap">
                  <img
                    src={resolveAssetUrl(selectedProgram.image_url)}
                    alt={selectedProgram.title}
                    loading="lazy"
                  />
                </div>
              ) : null}

              {displayDescription ? (
                <section className="program-details__section">
                  <h2>{t("programDetails.sections.overview")}</h2>
                  <p>{displayDescription}</p>
                </section>
              ) : null}

              {selectedProgram.points?.length ? (
                <section className="program-details__section">
                  <h2>{t("programDetails.sections.learning")}</h2>
                  <ul className="program-details__list">
                    {selectedProgram.points.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </section>
              ) : null}

              {visibleLevels.length ? (
                <section className="program-details__section">
                  <h2>
                    {t(
                      selectedLevelId
                        ? "programDetails.sections.levels.single"
                        : "programDetails.sections.levels.plural",
                    )}
                  </h2>
                  {!selectedLevelId ? (
                    shouldRequireLevel ? (
                      <p className="program-details__muted">
                        {t("programDetails.levels.pickFirst")}
                      </p>
                    ) : null
                  ) : null}
                  <div className="program-details__topics">
                    {visibleLevels.map((level) => (
                      <article
                        key={`level-${level.grade}`}
                        className="program-details__topic"
                      >
                        <div className="program-details__topicBody">
                          <h3>
                            {t("programDetails.levels.levelTitle", {
                              grade: level.grade,
                            })}
                          </h3>
                          {level.label ? <p>{level.label}</p> : null}
                        </div>
                      </article>
                    ))}
                  </div>
                  {!selectedLevelId ? (
                    shouldRequireLevel ? (
                      <div className="program-details__cta">
                        <Link
                          to={`/programs/${selectedProgram.slug || selectedProgram.id}/select-level`}
                          className="btn ghost"
                        >
                          {t("programDetails.levels.chooseLevel")}
                        </Link>
                      </div>
                    ) : null
                  ) : null}
                </section>
              ) : null}

              <div className="program-details__cta">
                <Link
                  to={selectedProgram.cta_link || "/signup"}
                  className="btn primary"
                >
                  {selectedProgram.cta_label ||
                    t("programDetails.cta.enrollNow")}
                </Link>
              </div>
            </main>

            <aside className="program-details__sidebar">
              {canEditPrograms && (
                <div className="program-details__editor">
                  <div className="program-details__editor-head">
                    <h4>{t("programDetails.sidebar.editTitle")}</h4>
                    <div className="program-details__editor-actions">
                      <button
                        type="button"
                        className="btn ghost"
                        onClick={() => setIsEditing(true)}
                      >
                        {t("programDetails.sidebar.editAction")}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}
